import { useState } from "react";
import { ScrollText, Inbox } from "lucide-react";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PageHeader, LoadingState, ErrorState, EmptyState, Badge, Card } from "@/components/common";
import { useFetch } from "@/lib/useFetch";
import { fmtDateTime } from "@/lib/format";
import { t } from "@/i18n";

const FILTERS = ["all", "garage", "subscription", "support_session", "plan", "employee", "invite", "customer"];

export function AdminAudit() {
  const [action, setAction] = useState("all");
  const { data, error, loading, reload } = useFetch("/admin/audit-log", action === "all" ? {} : { action });
  return (
    <div>
      <PageHeader eyebrow={t("admin.area")} title={t("admin.nav.audit")} subtitle={t("audit.subtitle")}
        actions={
          <Select value={action} onValueChange={setAction}>
            <SelectTrigger className="w-56" data-testid="audit-filter-select"><SelectValue /></SelectTrigger>
            <SelectContent>{FILTERS.map((f) => <SelectItem key={f} value={f}>{t(`audit.filter.${f}`)}</SelectItem>)}</SelectContent>
          </Select>
        } />
      {loading && !data && <LoadingState />}
      {error && <ErrorState message={error} onRetry={reload} />}
      {data?.length === 0 && <EmptyState icon={ScrollText} title={t("audit.empty")} />}
      {data?.length > 0 && (
        <div className="gf-card overflow-x-auto">
          <Table data-testid="audit-log-table">
            <TableHeader>
              <TableRow>
                <TableHead>{t("audit.when")}</TableHead>
                <TableHead>{t("audit.actor")}</TableHead>
                <TableHead>{t("audit.action")}</TableHead>
                <TableHead>{t("admin.garage")}</TableHead>
                <TableHead>{t("audit.details")}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.map((r) => (
                <TableRow key={r.id}>
                  <TableCell className="whitespace-nowrap text-xs">{fmtDateTime(r.created_at)}</TableCell>
                  <TableCell className="text-sm">{r.actor_email}</TableCell>
                  <TableCell><Badge tone={r.action.startsWith("support_session") ? "amber" : "navy"}>{r.action}</Badge></TableCell>
                  <TableCell className="text-sm">{r.garage_name || "—"}<span className="block text-xs text-slate-500">{r.sender_name}</span></TableCell>
                  <TableCell className="max-w-xs truncate font-mono text-xs text-slate-600" title={JSON.stringify(r.details)}>{Object.entries(r.details || {}).map(([k, v]) => `${k}: ${typeof v === "object" ? JSON.stringify(v) : v}`).join(" · ") || "—"}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}

export function AdminOutbox() {
  const { data, error, loading, reload } = useFetch("/admin/outbox");
  return (
    <div>
      <PageHeader eyebrow={t("admin.area")} title={t("admin.nav.outbox")} subtitle={t("outbox.subtitle")} />
      <Card className="mb-6 border-amber-200 bg-amber-50 p-4" testId="outbox-warning">
        <p className="text-sm text-amber-900">{t("outbox.warning")}</p>
      </Card>
      {loading && !data && <LoadingState />}
      {error && <ErrorState message={error} onRetry={reload} />}
      {data?.length === 0 && <EmptyState icon={Inbox} title={t("outbox.empty")} />}
      {data?.length > 0 && (
        <div className="gf-card overflow-x-auto">
          <Table data-testid="outbox-table">
            <TableHeader>
              <TableRow>
                <TableHead>{t("audit.when")}</TableHead>
                <TableHead>{t("outbox.recipient")}</TableHead>
                <TableHead>{t("outbox.template")}</TableHead>
                <TableHead>{t("admin.garage")}</TableHead>
                <TableHead>{t("outbox.token")}</TableHead>
                <TableHead>{t("outbox.status")}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.map((r) => (
                <TableRow key={r.id} data-testid="outbox-row">
                  <TableCell className="whitespace-nowrap text-xs">{fmtDateTime(r.created_at)}</TableCell>
                  <TableCell className="text-sm">{r.to}</TableCell>
                  <TableCell><Badge tone="navy">{t(`outbox.templates.${r.template}`)}</Badge></TableCell>
                  <TableCell className="text-sm">{r.garage_name || "—"}<span className="block text-xs text-slate-500">{r.sender_name}</span></TableCell>
                  <TableCell className="font-mono text-xs text-slate-500">{r.token}</TableCell>
                  <TableCell><Badge tone={r.status === "sent" ? "green" : ["failed", "blocked"].includes(r.status) ? "red" : "amber"} testId="outbox-status">{r.status}</Badge>{r.error && <span className="block max-w-xs truncate text-xs text-red-600" title={r.error}>{r.error}</span>}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}
